import { useMemo, useState } from 'react';
import type { Board } from '../lib/types';
import { hhmm } from '../lib/format';

/**
 * Work that no technician is going to reach.
 *
 * The most actionable list on the screen: every row here is a customer who will
 * not be visited unless a dispatcher does something about it. Sorted by priority
 * so the expensive omissions surface first.
 *
 * Each row is also the start of a drag onto a lane. The solver is right to
 * refuse work that does not fit, and a dispatcher is sometimes right to overrule
 * it -- they know the visit is quick, or that they will ring ahead. Without the
 * drag this panel is a list of problems with no way to act on any of them.
 */
export function UnassignedPanel({
  board,
  onFocus,
  onPickUp,
  onFindFit,
}: {
  board: Board | null;
  onFocus: (orderId: string) => void;
  /**
   * A press on a row, handed to the routes panel to continue as a drag.
   *
   * Reported rather than handled here: the lanes, the ruler and the drop
   * resolution all live over there, and a second implementation of dropping
   * would be a second set of bugs.
   */
  onPickUp?: (order: { id: string; name: string }, x: number, y: number) => void;
  /** Preview where this order would best go, and place it from there. */
  onFindFit?: (order: { id: string; name: string }) => void;
}) {
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    if (!board) return [];
    const needle = query.trim().toLowerCase();
    return board.unassigned
      .filter((o) => (needle ? o.name.toLowerCase().includes(needle) : true))
      .slice(0, 300);
  }, [board, query]);

  return (
    <aside className="side">
      <div className="side-head">
        <h2>Unassigned</h2>
        <span className="count-pill">{board?.unassigned.length ?? 0}</span>
      </div>

      <div className="search">
        <input
          type="text"
          value={query}
          placeholder="Search orders"
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search unassigned orders"
        />
      </div>

      <div className="side-list">
        {rows.length === 0 ? (
          <div className="empty">
            {board?.unassigned.length === 0 ? 'Every order is on a route.' : 'Nothing matches that search.'}
          </div>
        ) : (
          rows.map((order) => (
            <div key={order.id} className="row-with-action">
              <div
                className="order-row"
                data-grab={onPickUp ? 'true' : undefined}
                role="button"
                tabIndex={0}
                /*
                 * The press starts a drag; a press that barely moves is treated as
                 * a click by the same threshold the stop chips use, so there is no
                 * onClick here to fire twice.
                 */
                onPointerDown={
                  onPickUp
                    ? (event) => {
                        if (event.button !== 0) return;
                        event.preventDefault();
                        onPickUp({ id: order.id, name: order.name }, event.clientX, event.clientY);
                      }
                    : undefined
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onFocus(order.id);
                  }
                }}
              >
                <b>{order.name}</b>
                <span className="delta" data-tone={order.priority >= 70 ? 'warn' : 'idle'}>
                  P{order.priority}
                </span>
                <small>
                  {order.timeWindows.length > 0
                    ? order.timeWindows.map((w) => `${hhmm(w.start)}-${hhmm(w.end)}`).join(', ')
                    : 'No time window'}
                </small>
                {/*
                 * Why the build left it out.
                 *
                 * Absent for an order that simply arrived after the last build --
                 * which is not the same as one the solver weighed and refused,
                 * and saying nothing is better than guessing which happened.
                 */}
                {order.reason ? <small className="order-reason">{order.reason}</small> : null}
              </div>
              {onFindFit ? (
                <FindFitButton onClick={() => onFindFit({ id: order.id, name: order.name })} />
              ) : null}
            </div>
          ))
        )}
      </div>
    </aside>
  );
}

/**
 * The row's own action. A sibling of the row rather than inside it: the row
 * starts a drag on press, and a button nested in it would start one too.
 */
export function FindFitButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="row-action"
      title="Find the best fit"
      aria-label="Find the best fit"
      onClick={onClick}
    >
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="5.2" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="8" cy="8" r="1.6" fill="currentColor" />
        <path
          d="M8 1v2.2M8 12.8V15M1 8h2.2M12.8 8H15"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}
